import { useEffect, useState } from "react";
import { api } from "../../api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "../../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter, SheetClose } from "../../components/ui/sheet";
import { useLanguage } from "../../contexts/LanguageContext";
import { settingsLabels } from "./labels";

export default function WhatsappConfigPage() {
  const { lang } = useLanguage();
  const t = settingsLabels[lang].whatsappConfig;
  const [sendType, setSendType] = useState<"daily" | "before" | "none">("daily");
  const [time, setTime] = useState("");
  const [hoursBefore, setHoursBefore] = useState("1");
  const [instanceId, setInstanceId] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [qrRaw, setQrRaw] = useState<any>(null);
  const [connected, setConnected] = useState(false);
  const [connStatus, setConnStatus] = useState<"unknown" | "open" | "closed" | "error">("unknown");

  useEffect(() => {
    const load = async () => {
      const res = await api.getCompanyWhatsappSettings();
      if (res.ok && res.data) {
        setSendType(res.data.type);
        if (res.data.time) setTime(res.data.time);
        if (res.data.hours_before != null)
          setHoursBefore(String(res.data.hours_before));
        if (res.data.instance_id) setInstanceId(res.data.instance_id);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkConnection = async (id: string) => {
    try {
      setConnStatus("unknown");
      console.log("[WhatsApp] Verificando status da instância:", id);
      const res = await api.getWhatsappState(id);
      console.log("[WhatsApp] Resposta do estado:", res);
      if (!res.ok) {
        setConnStatus("error");
        return;
      }
      const state = (res.data as any)?.instance?.state || (res.data as any)?.state;
      if (typeof state === "string" && state.toLowerCase() === "open") {
        setConnStatus("open");
        setConnected(true);
      } else {
        setConnStatus("closed");
        setConnected(false);
      }
    } catch (e) {
      setConnStatus("error");
    }
  };

  // Quando o instanceId carregar/alterar, verifica status automaticamente
  useEffect(() => {
    if (!instanceId) return;
    checkConnection(instanceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  const save = async () => {
    const res = await api.updateCompanyWhatsappSettings({
      type: sendType,
      time: sendType === "daily" ? time : null,
      hours_before: sendType === "before" ? Number(hoursBefore) : null,
      instance_id: instanceId || null,
    });
    if (!res.ok) {
      toast.error(res.error || t.toasts.saveError);
      return;
    }
    toast.success(t.toasts.saved);
  };

  const connectWhatsapp = async () => {
    try {
      setConnecting(true);
      console.log("[WhatsApp] Iniciando conexão...");
      const id = (globalThis.crypto && "randomUUID" in globalThis.crypto)
        ? globalThis.crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
      setInstanceId(id);
      console.log("[WhatsApp] UUID gerado (instance_id)", id);
      console.log("[WhatsApp] Chamando api.getWhatsappQrCode...");
      const res = await api.getWhatsappQrCode(id);
      console.log("[WhatsApp] Resposta da API:", res);
      if (!res.ok) {
        console.error("[WhatsApp] Erro na conexão:", res.error);
        toast.error(res.error || t.toasts.connectError);
        return;
      }
      // Extrai o base64 para exibir no drawer
      const data: any = (res as any).data;
      const payload = data?.payload ?? data;
      let img: string | null = null;
      if (Array.isArray(payload) && payload.length > 0) {
        img = payload[0]?.base64 || null;
      } else if (payload?.base64) {
        img = payload.base64;
      }
      setQrRaw(payload);
      if (img && typeof img === "string" && img.startsWith("data:image")) {
        setQrImage(img);
        setQrOpen(true);
        toast.success(t.toasts.qrGenerated);
      } else {
        toast.success(t.toasts.requestSent);
      }
    } catch (e: any) {
      console.error("[WhatsApp] Exceção ao conectar:", e);
      toast.error(e?.message || t.toasts.connectError);
    } finally {
      console.log("[WhatsApp] Finalizando tentativa de conexão");
      setConnecting(false);
    }
  };

  // Polling do estado de conexão enquanto o QR está aberto
  useEffect(() => {
    if (!qrOpen || !instanceId) return;
    let cancelled = false;
    let timer: any;
    const tick = async () => {
      try {
        const res = await api.getWhatsappState(instanceId);
        console.log("[WhatsApp] Estado da conexão:", res);
        if (res.ok) {
          const state = (res.data as any)?.instance?.state || (res.data as any)?.state;
          if (typeof state === "string" && state.toLowerCase() === "open") {
            if (!cancelled) {
              setConnected(true);
              setConnStatus("open");
              setQrOpen(false);
              toast.success("WhatsApp conectado com sucesso.");
              // Salva automaticamente o instance_id junto com as configurações atuais
              const saveRes = await api.updateCompanyWhatsappSettings({
                type: sendType,
                time: sendType === "daily" ? time : null,
                hours_before: sendType === "before" ? Number(hoursBefore) : null,
                instance_id: instanceId,
              });
              if (!saveRes.ok) {
                toast.error(saveRes.error || "Falha ao salvar instance_id");
              } else {
                toast.success("Instância salva automaticamente.");
              }
            }
            return; // para de agendar novo tick
          }
        }
      } catch (e) {
        console.error("[WhatsApp] Erro ao consultar estado:", e);
      }
      if (!cancelled) timer = setTimeout(tick, 5000);
    };
    timer = setTimeout(tick, 5000);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [qrOpen, instanceId]);

  const isConnected = connStatus === "open";

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 p-4">
      {/* Estado de conexão no topo, seguindo o layout do print */}
      {isConnected ? (
        <div className="rounded-md bg-green-50 border border-green-200 text-green-800 px-3 py-2">
          <div className="font-medium">{t.states.connected}</div>
          <div className="text-sm">{t.fields.instanceId}: {instanceId}</div>
        </div>
      ) : (
        <div className="rounded-md bg-white border border-neutral-200 text-neutral-800 px-4 py-3 flex items-center justify-between">
          <span>{t.states.disconnected}</span>
          <Button
            variant="default"
            onClick={connectWhatsapp}
            disabled={connecting || (instanceId ? connStatus === "unknown" : false)}
          >
            {connecting ? t.states.connecting : t.buttons.connect}
          </Button>
        </div>
      )}

      {/* Formulário somente quando conectado, conforme o print */}
      {isConnected && (
      <>
      <h1 className="text-xl font-semibold">{t.title}</h1>
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="block text-sm font-medium">Tipo do envio</label>
          <div className="grid gap-2">
            <Label
              htmlFor="send-type-daily"
              className="hover:bg-accent/50 flex items-start gap-3 rounded-lg border p-3 has-[[aria-checked=true]]:border-green-600 has-[[aria-checked=true]]:bg-green-600/30 dark:has-[[aria-checked=true]]:border-green-600 dark:has-[[aria-checked=true]]:bg-green-600/30"
            >
              <Checkbox
                id="send-type-daily"
                checked={sendType === "daily"}
                onCheckedChange={(checked) => checked && setSendType("daily")}
                className="data-[state=checked]:border-green-600 data-[state=checked]:bg-green-600 data-[state=checked]:text-white dark:data-[state=checked]:border-green-700 dark:data-[state=checked]:bg-green-700"
              />
              <div className="grid gap-1.5 font-normal">
                <p className="text-sm leading-none font-medium">{t.sendTypes.daily.title}</p>
                <p className="text-muted-foreground text-sm">{t.sendTypes.daily.desc}</p>
              </div>
            </Label>
            <Label
              htmlFor="send-type-before"
              className="hover:bg-accent/50 flex items-start gap-3 rounded-lg border p-3 has-[[aria-checked=true]]:border-green-600 has-[[aria-checked=true]]:bg-green-600/30 dark:has-[[aria-checked=true]]:border-green-600 dark:has-[[aria-checked=true]]:bg-green-600/30"
            >
              <Checkbox
                id="send-type-before"
                checked={sendType === "before"}
                onCheckedChange={(checked) => checked && setSendType("before")}
                className="data-[state=checked]:border-green-600 data-[state=checked]:bg-green-600 data-[state=checked]:text-white dark:data-[state=checked]:border-green-700 dark:data-[state=checked]:bg-green-700"
              />
              <div className="grid gap-1.5 font-normal">
                <p className="text-sm leading-none font-medium">{t.sendTypes.before.title}</p>
                <p className="text-muted-foreground text-sm">{t.sendTypes.before.desc}</p>
              </div>
            </Label>
            <Label
              htmlFor="send-type-none"
              className="hover:bg-accent/50 flex items-start gap-3 rounded-lg border p-3 has-[[aria-checked=true]]:border-green-600 has-[[aria-checked=true]]:bg-green-600/30 dark:has-[[aria-checked=true]]:border-green-600 dark:has-[[aria-checked=true]]:bg-green-600/30"
            >
              <Checkbox
                id="send-type-none"
                checked={sendType === "none"}
                onCheckedChange={(checked) => checked && setSendType("none")}
                className="data-[state=checked]:border-green-600 data-[state=checked]:bg-green-600 data-[state=checked]:text-white dark:data-[state=checked]:border-green-700 dark:data-[state=checked]:bg-green-700"
              />
              <div className="grid gap-1.5 font-normal">
                <p className="text-sm leading-none font-medium">{t.sendTypes.none.title}</p>
                <p className="text-muted-foreground text-sm">{t.sendTypes.none.desc}</p>
              </div>
            </Label>
          </div>
        </div>
        {sendType === "daily" ? (
          <div className="space-y-1">
            <label className="block text-sm font-medium">{t.fields.time}</label>
            <Input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </div>
        ) : sendType === "before" ? (
          <div className="space-y-1">
            <label className="block text-sm font-medium">{t.fields.hoursBefore}</label>
            <Input
              type="number"
              min={0}
              value={hoursBefore}
              onChange={(e) => setHoursBefore(e.target.value)}
            />
          </div>
        ) : null}
        <div className="space-y-1">
          <label className="block text-sm font-medium">{t.fields.instanceId}</label>
          <div className="text-sm text-neutral-800 break-all">{instanceId || "-"}</div>
        </div>
        <div className="flex gap-2">
          <Button onClick={save}>{t.buttons.save}</Button>
        </div>
      </div>
      </>
      )}

      {/* Drawer para exibir o QR Code */}
      <Sheet open={qrOpen} onOpenChange={setQrOpen}>
        <SheetContent side="right">
          <SheetHeader>
            <SheetTitle>{t.qrDialog.title}</SheetTitle>
            <SheetClose asChild>
              <Button variant="ghost">{t.buttons.close}</Button>
            </SheetClose>
          </SheetHeader>
      <div className="p-4 space-y-4">
        <SheetDescription>
          {t.qrDialog.description}
        </SheetDescription>
        {qrImage ? (
          <img
            src={qrImage}
            alt={t.qrDialog.alt}
            className="w-full max-w-sm border rounded-md"
          />
        ) : (
          <div className="text-sm text-neutral-600">{t.toasts.noQr}</div>
        )}
        <details className="text-xs text-neutral-500">
          <summary>{t.toasts.details}</summary>
          <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all bg-neutral-50 border p-2 rounded">
            {qrRaw ? JSON.stringify(qrRaw, null, 2) : "Sem dados"}
          </pre>
        </details>
      </div>
      <SheetFooter>
        <SheetClose asChild>
          <Button>{t.qrDialog.close}</Button>
        </SheetClose>
      </SheetFooter>
    </SheetContent>
  </Sheet>
    </div>
  );
}
